<template>
  <div class="page page-day-report-v2">
    <!-- Top Bar with Logo and Download Button -->
    <div class="custom-top-bar">
      <div class="brand-area">
        <img :src="icons.logo" class="brand-logo" alt="logo" />
        <span class="brand-text">Kukukao</span>
      </div>
      <button class="btn-download" @click="handleOpenApp">下载app</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-text">{{ error }}</div>
      <button class="btn-retry" @click="fetchDayReportStats">重试</button>
    </div>

    <div v-else class="content-wrapper">
      <!-- Yellow/Orange Gradient Header -->
      <div class="header-bg"></div>

      <div class="phone">
        <div class="phone-inner">
          <!-- Experience Card -->
            <div class="card experience-card">
              <div class="card-header">
                <div class="card-title">
                  <img :src="icons.experienceIcon" class="title-icon" alt="" />
                  今日经验值
                </div>
                <div class="card-value">
                <span class="value-number">+{{ totalExperience }}</span>
                <span class="value-unit">经验</span>
              </div>
            </div>
            <div class="card-divider"></div>
              <div class="card-categories">
                <div class="category-item">
                  <span class="category-label">真题</span>
                  <span class="category-value real">{{ experience.realQuestionExperience }}经验</span>
                </div>
                <div class="category-item">
                  <span class="category-label">基础</span>
                  <span class="category-value basic">{{ experience.basicQuestionExperience }}经验</span>
                </div>
                <div class="category-item">
                  <span class="category-label">kuku记</span>
                  <span class="category-value kuku">{{ experience.kukuMemoryExperience }}经验</span>
                </div>
              </div>
            </div>

          <!-- Duration Card -->
            <div class="card duration-card">
              <div class="card-header">
                <div class="card-title">
                  <img :src="icons.durationIcon" class="title-icon" alt="" />
                  今日学习用时
                </div>
              <div class="card-value">
                <span class="value-number blue">{{ totalDuration }}</span>
                <span class="value-unit blue">分钟</span>
              </div>
            </div>
            <div class="card-divider"></div>
              <div class="card-categories">
                <div class="category-item">
                  <span class="category-label">真题</span>
                  <span class="category-value real">{{ duration.realQuestion }}分钟</span>
                </div>
                <div class="category-item">
                  <span class="category-label">基础</span>
                  <span class="category-value basic">{{ duration.basicQuestion }}分钟</span>
                </div>
                <div class="category-item">
                  <span class="category-label">kuku记</span>
                  <span class="category-value kuku">{{ duration.kukuMemory }}分钟</span>
                </div>
              </div>
          </div>

          <!-- Tab Switcher -->
          <div class="tab-switcher">
            <div
              v-for="tab in tabs"
                :key="tab.key"
                class="tab-item"
                :class="{ active: activeTab === tab.key }"
                @click="setActiveTab(tab.key)"
              >
              {{ tab.label }}
            </div>
          </div>

          <!-- Answer Stats -->
            <div class="card stats-card">
              <div class="stat-item">
                <img :src="icons.answerIcon" class="stat-icon" alt="" />
                <div class="stat-content">
                  <div class="stat-number">{{ currentAnswerStats.answerCount }}</div>
                  <div class="stat-label">答题数</div>
                </div>
              </div>
              <div class="stat-item">
                <img :src="icons.accuracyIcon" class="stat-icon" alt="" />
                <div class="stat-content">
                  <div class="stat-number">{{ currentAnswerStats.accuracy }}%</div>
                  <div class="stat-label">准确率</div>
                </div>
              </div>
            </div>

          <!-- Battle Record Section -->
          <div class="card battle-card">
            <div class="battle-header">
              <span class="battle-title">今日战绩</span>
              <div class="battle-tabs">
                  <span
                    v-for="type in battleTabs"
                    :key="type.key"
                    class="battle-tab"
                    :class="{ active: activeBattleType === type.key }"
                    @click="setBattleType(type.key)"
                  >
                  {{ type.label }}
                </span>
              </div>
            </div>

              <div class="battle-list">
                <div v-if="displayedBattleRecords.length === 0" class="empty-battle">
                  今天暂时还没有战绩
                </div>
                <div
                  v-for="(record, index) in displayedBattleRecords"
                  :key="index"
                  class="battle-item"
                >
                  <div class="battle-info">
                    <div class="battle-tags">
                      <span class="battle-subject" :style="getSubjectStyle(record)">{{ record.subjectName || '科目' }}</span>
                      <span v-if="activeTab === 'basic'" class="battle-chapter">第{{ record.stageCode || 0 }}关</span>
                      <span v-else-if="record.year" class="battle-year">{{ record.year }}</span>
                    </div>
                    <div class="battle-name">{{ getBattleTitle(record) }}</div>
                  </div>
                  <div class="battle-count">
                    <span class="count-label">{{ activeTab === 'basic' ? '通关次数：' : '答题次数：' }}</span>
                    <span class="count-value">{{ getBattleCount(record) }}</span>
                  </div>
                </div>

                <div
                  v-if="hasMoreBattleRecords && !showAllStages"
                  class="battle-more"
                  @click="showAllStages = !showAllStages"
                >
                  {{ showAllStages ? '收起' : '更多' }}
                  <span class="arrow">▼</span>
                </div>
                <button
                  v-else-if="hasMoreBattleRecords"
                  class="battle-open-btn"
                  @click="handleOpenApp"
                >
                  打开app查看更多
                </button>
              </div>
            </div>

          <!-- Kuku Learning Diagnosis -->
          <div class="card diagnosis-card" v-if="diagnosisText">
            <div class="diagnosis-header">
              <img :src="icons.kukuIcon" class="diagnosis-icon" alt="" />
              <span class="diagnosis-title">kuku学习诊断</span>
            </div>
            <div class="diagnosis-content" v-html="formatDiagnosisText(diagnosisText)"></div>
          </div>

          <!-- Quote Section -->
          <div class="card quote-card" v-if="encouragingWords">
            <div class="quote-mark">"</div>
            <div class="quote-text">{{ encouragingWords }}</div>
          </div>

          <!-- Bottom Button -->
          <div class="bottom-btn-wrapper">
            <button class="bottom-btn" @click="handleOpenApp">
              打开app查看
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { homeImages } from '../homeImages'

// Import assets
import logo from '@/assets/day/icon_dayv2_logo.png'
import iconAnswer from '@/assets/day/icon_count.svg'
import iconAccuracy from '@/assets/day/icon_accuracy.svg'
import iconTime from '@/assets/day/icon_time.svg'
import iconExperience from '@/assets/day/icon_experience_star.png'
import kukuIcon from '@/assets/day/icon_kuku_diagnosis.png'

import { openApp } from '@/utils/openApp'

export default {
  name: 'HomeDayReportV2',
  props: {
    query: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      images: homeImages,
      icons: {
          logo,
          answerIcon: iconAnswer,
          accuracyIcon: iconAccuracy,
          durationIcon: iconTime,
          experienceIcon: iconExperience,
          kukuIcon
        },
      loading: true,
      error: null,
      reportData: {},
      activeTab: 'basic',
      activeBattleType: 'standard',
      showAllStages: false,
        tabs: [
          { key: 'basic', label: '基础' },
          { key: 'real', label: '真题' }
        ],
        basicBattleTabs: [
          { key: 'standard', label: '标准关卡' },
          { key: 'quick', label: '快速记忆' },
          { key: 'reinforce', label: '巩固一下' }
        ],
        realBattleTabs: [
          { key: 'paperOne', label: '卷一' },
          { key: 'paperTwo', label: '卷二' }
        ],
        useMock: false
      }
    },
    computed: {
    reportId() {
      return this.query.id || (this.$route && this.$route.query && this.$route.query.id) || null
    },
    // Experience data
    experience() {
      const dashboard = this.reportData.dashboard || {}
      const exp = dashboard.experience || {}
      return {
        totalExperience: exp.totalExperience || 0,
        realQuestionExperience: exp.realQuestionExperience || 0,
        basicQuestionExperience: exp.basicQuestionExperience || 0,
        kukuMemoryExperience: exp.kukuMemoryExperience || 0
      }
    },
    totalExperience() {
      return this.experience.totalExperience
    },
    // Duration data (convert seconds to minutes)
    duration() {
      const dashboard = this.reportData.dashboard || {}
      const dur = dashboard.duration || {}
      return {
        total: this.formatDurationMinute(dur.totalStudyDurationSec),
        realQuestion: this.formatDurationMinute(dur.realQuestionStudyDurationSec),
        basicQuestion: this.formatDurationMinute(dur.basicQuestionStudyDurationSec),
        kukuMemory: this.formatDurationMinute(dur.kukuMemoryStudyDurationSec)
      }
    },
    totalDuration() {
      return this.duration.total
    },
    // Answer stats based on active tab
      currentAnswerStats() {
        const dashboard = this.reportData.dashboard || {}
        const stats = dashboard.answerStats || {}
        const realQ = stats.realQuestion || {}
        const basicQ = stats.basicQuestion || {}
        const currentStats = this.activeTab === 'real' ? realQ : basicQ

        return {
          answerCount: currentStats.answerCount || 0,
          accuracy: this.formatAccuracy(currentStats.accuracy)
        }
      },
      battleTabs() {
        return this.activeTab === 'basic' ? this.basicBattleTabs : this.realBattleTabs
      },
      currentBattleRecords() {
        if (this.activeTab === 'real') {
          const kpStats = this.reportData.realQuestionKpAnswerStats || {}
          const paperStats = kpStats[this.activeBattleType] || {}
          return paperStats.records || []
        }

        const stagePassStats = this.reportData.stagePassStats || {}
        const stageStats = stagePassStats[this.activeBattleType] || {}
        return stageStats.records || []
      },
      displayedBattleRecords() {
        return this.showAllStages ? this.currentBattleRecords : this.currentBattleRecords.slice(0, 2)
      },
      hasMoreBattleRecords() {
        return this.currentBattleRecords.length > 2
      },
    parsedStudyDiagnosis() {
      const diagnosis = this.reportData.studyDiagnosis
      if (!diagnosis) return null

      if (typeof diagnosis === 'object') return diagnosis

      if (typeof diagnosis === 'string') {
        const trimmed = diagnosis.trim()
        if (!trimmed) return null

        try {
          return JSON.parse(trimmed)
        } catch (e) {
          return { learningDiagnosis: trimmed }
        }
      }

      return null
    },
    diagnosisText() {
      const parsed = this.parsedStudyDiagnosis
      if (parsed && parsed.learningDiagnosis) {
        return parsed.learningDiagnosis
      }

      // Combine paperOne and paperTwo records for diagnosis
      const kpStats = this.reportData.realQuestionKpAnswerStats || {}
      const paperOneData = kpStats.paperOne || {}
      const paperTwoData = kpStats.paperTwo || {}
      const paperOne = paperOneData.records || []
      const paperTwo = paperTwoData.records || []

      if (paperOne.length === 0 && paperTwo.length === 0) {
        return ''
      }

      // Build diagnosis text based on knowledge points
      const allKps = [...paperOne, ...paperTwo]
      if (allKps.length === 0) return ''

      // Get top knowledge points with most answers
      const topKps = allKps
        .sort(function(a, b) { return (b.answerCount || 0) - (a.answerCount || 0) })
        .slice(0, 3)

      const kpTexts = topKps.map(function(kp) {
        return '**' + (kp.knowledgePoint || '未知知识点') + '**（' + (kp.subjectName || '') + '）共练习' + (kp.answerCount || 0) + '次'
      })

      return '今日你在' + kpTexts.join('、') + '方面投入较多精力，建议继续保持学习节奏，巩固薄弱知识点。'
    },
    // Encouraging words
    encouragingWords() {
      const dashboard = this.reportData.dashboard || {}
      return dashboard.encouragingWords || '每一个不起眼的坚持，都是在为未来的法考之路铺路。下周继续冲！'
    }
  },
    mounted() {
      this.fetchDayReportStats()
    },
    watch: {
      activeTab() {
        const firstBattleTab = this.battleTabs[0]
        this.activeBattleType = firstBattleTab ? firstBattleTab.key : ''
        this.showAllStages = false
      }
    },
    methods: {
      async fetchDayReportStats() {
      if (!this.reportId) {
        this.error = '缺少报告ID参数'
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

        if (this.useMock || this.query.mock === '1') {
          setTimeout(() => {
            this.reportData = this.getMockData()
          this.loading = false
        }, 500)
        return
      }

      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL || ''}/share/getDayReportStats?id=${this.reportId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const result = await response.json()

          if (this.isSuccessCode(result.code) && result.data) {
            this.reportData = result.data
        } else {
          this.error = result.message || '获取数据失败'
        }
      } catch (err) {
        console.error('获取日报统计失败:', err)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    isSuccessCode(code) {
      return code === 0 || code === 200 || code === '0' || code === '200'
    },
    getMockData() {
      return {
        hasData: true,
        dayReportId: 2386,
        userId: 123,
        reportDate: '2026-08-07',
        dashboard: {
          statDate: '2026-08-07',
          experience: {
            totalExperience: 240,
            realQuestionExperience: 72,
            basicQuestionExperience: 124,
            kukuMemoryExperience: 44
          },
          duration: {
            totalStudyDurationSec: 840,
            realQuestionStudyDurationSec: 300,
            basicQuestionStudyDurationSec: 420,
            kukuMemoryStudyDurationSec: 120
          },
          answerStats: {
            basicQuestion: {
              answerCount: 24,
              correctCount: 22,
              accuracy: 92
            },
            realQuestion: {
              answerCount: 18,
              correctCount: 16,
              accuracy: 89
            }
          },
          encouragingWords: '每一个不起眼的坚持，都是在为未来的法考之路铺路。下周继续冲！'
        },
        studyDiagnosis: JSON.stringify({
          learningDiagnosis: '【基础题】本周完成56题，准确率77.27%，状态稳定。建议继续保持学习节奏，巩固高频知识点。'
        }),
        stagePassStats: {
          standard: {
            size: 10,
            current: 1,
            records: [
              {
                subjectId: 1,
                subjectName: '民法',
                stageId: 101,
                stageCode: 28,
                stageMainTitle: '要约与承诺辨析',
                passCount: 2,
                latestPassTime: '2026-08-07T10:30:00.000Z'
              },
              {
                subjectId: 1,
                subjectName: '民法',
                stageId: 102,
                stageCode: 28,
                stageMainTitle: '要约与承诺辨析',
                passCount: 2,
                latestPassTime: '2026-08-07T10:30:00.000Z'
              },
              {
                subjectId: 2,
                subjectName: '刑法',
                stageId: 201,
                stageCode: 24,
                stageMainTitle: '正当防卫',
                passCount: 2,
                latestPassTime: '2026-08-07T09:15:00.000Z'
              },
              {
                subjectId: 2,
                subjectName: '刑法',
                stageId: 202,
                stageCode: 25,
                stageMainTitle: '诚信原则',
                passCount: 1,
                latestPassTime: '2026-08-07T08:30:00.000Z'
              }
            ],
            pages: 1,
            total: 4
          },
          quick: {
            size: 10,
            current: 1,
            records: [],
            pages: 0,
            total: 0
          },
          reinforce: {
            size: 10,
            current: 1,
            records: [],
            pages: 0,
            total: 0
          }
        },
        realQuestionKpAnswerStats: {
          paperOne: {
            size: 10,
            current: 1,
            records: [
              {
                subjectId: 1,
                subjectName: '民法',
                paperDescription: '2024年真题',
                year: 2024,
                kpTreeId: 1001,
                knowledgePoint: '物权担保',
                answerCount: 8,
                latestAnswerTime: '2026-08-07T10:30:00.000Z'
              },
              {
                subjectId: 1,
                subjectName: '民法',
                paperDescription: '2024年真题',
                year: 2024,
                kpTreeId: 1002,
                knowledgePoint: '抵押权的实现顺序',
                answerCount: 5,
                latestAnswerTime: '2026-08-07T09:15:00.000Z'
              }
            ],
            pages: 1,
            total: 2
          },
          paperTwo: {
            size: 10,
            current: 1,
            records: [
              {
                subjectId: 2,
                subjectName: '刑法',
                paperDescription: '2024年真题',
                year: 2024,
                kpTreeId: 2001,
                knowledgePoint: '正当防卫',
                answerCount: 3,
                latestAnswerTime: '2026-08-07T08:30:00.000Z'
              }
            ],
            pages: 1,
            total: 1
          }
        }
      }
      },
      setActiveTab(key) {
        if (this.activeTab === key) return
        this.activeTab = key
      },
      setBattleType(key) {
        this.activeBattleType = key
        this.showAllStages = false
      },
      formatAccuracy(value) {
        const number = Number(value || 0)
        if (!Number.isFinite(number)) return 0
        return number <= 1 && number > 0 ? Math.round(number * 100) : Math.round(number)
      },
      getBattleTitle(record) {
        if (this.activeTab === 'real') {
          return record.knowledgePoint || record.paperDescription || '知识点'
        }
        return record.stageMainTitle || '关卡'
      },
    getBattleCount(record) {
      return this.activeTab === 'real' ? (record.answerCount || 0) : (record.passCount || 0)
    },
    getSubjectStyle(record) {
      return record.subjectColor ? { backgroundColor: record.subjectColor } : null
    },
    formatDurationMinute(seconds) {
      const value = Number(seconds || 0)
      return value > 0 ? Math.ceil(value / 60) : 0
    },
    formatDiagnosisText(text) {
      if (!text) return ''
      // 先转义 HTML 特殊字符，防止 XSS
      let html = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // 处理 **加粗**，v-html 插入的节点需要配合 ::v-deep 样式生效
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="diag-strong">$1</strong>')
      // 处理颜色标签 {{文字|颜色}}
      html = html.replace(/\{\{(.+?)\|([^}]+)\}\}/g, function (_, text, color) {
        var safe = String(color).trim()
        if (/^(#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\)|[a-zA-Z]+)$/.test(safe)) {
          return '<span style="color:' + safe + '">' + text + '</span>'
        }
        return text
      })
      // 处理无序列表
      const lines = html.split('\n')
      let result = []
      let inList = false
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const listMatch = line.match(/^\s*[-*]\s+(.*)$/)
        if (listMatch) {
          if (!inList) {
            result.push('<ul class="diag-list">')
            inList = true
          }
          result.push('<li>' + listMatch[1] + '</li>')
        } else {
          if (inList) {
            result.push('</ul>')
            inList = false
          }
          const indented = line.replace(/^(\s+)/, function (m) {
            return new Array(m.length + 1).join('&nbsp;')
          })
          result.push(indented)
        }
      }
      if (inList) result.push('</ul>')
      html = result.join('\n')
      html = html.replace(/\n/g, '<br/>')
      html = html.replace(/(<\/ul>)<br\/>/g, '$1')
      html = html.replace(/<br\/>(<li>)/g, '$1')
      return html
    },
    handleOpenApp() {
      openApp()
    }
  }
}
</script>

<style lang="scss" scoped>
.page-day-report-v2 {
  min-height: 100vh;
  background: #EFEFEF;
  padding-bottom: 30px;
  padding-top: calc(56px + env(safe-area-inset-top, 0px));
  position: relative;
  overflow-x: hidden;
  width: 100%;
}

.phone {
  padding: 0 20px 12px;
}

.phone-inner {
  position: relative;
  z-index: 1;
}

/* Header Background */
.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 360px;
  background: linear-gradient(180deg, #FFB62C 0%, #FFC861 45%, #FFE5A7 78%, #EFEFEF 100%);
  z-index: 0;
}

/* Custom Top Bar */
.custom-top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(11px + env(safe-area-inset-top, 0px)) 22px 11px 22px;
  background: #fff;
}

.brand-area {
  display: flex;
  align-items: center;

  .brand-logo {
    width: 38px;
    height: 38px;
    margin-right: 7px;
    object-fit: contain;
  }
  .brand-text {
    font-size: 20px;
    font-weight: 700;
    color: #f15b5b;
  }
}

.btn-download {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 99px;
  padding: 11px 26px;
  font-size: 14px;
  font-weight: 600;
}

/* Cards */
.card {
  background: #fff;
  border-radius: 20px;
  padding: 18px 20px;
  margin-bottom: 12px;
  box-shadow: none;
}

/* Experience Card */
.experience-card {
  margin-top: 28px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.card-title {
  display: flex;
  align-items: center;
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.title-icon {
  width: 26px;
  height: 26px;
  margin-right: 8px;
  object-fit: contain;
}

.card-value {
  display: flex;
  align-items: baseline;
}

.value-number {
  font-size: 20px;
  font-weight: 800;
  color: #F86666;
}

.value-unit {
  font-size: 20px;
  font-weight: 800;
  color: #F86666;
  margin-left: 2px;
}

.blue {
  color: #5DA2FF !important;
}

.card-divider {
  height: 1px;
  background: #F0F0F0;
  margin: 0 0 14px;
}

.card-categories {
  display: flex;
  justify-content: space-between;
}

.category-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  & + .category-item::before {
    content: "";
    position: absolute;
    left: 0;
    top: 6px;
    width: 1px;
    height: 42px;
    background: #F0F0F0;
  }
}

.category-label {
  font-size: 14px;
  color: #333;
  font-weight: 700;
  margin-bottom: 8px;
}

.category-value {
  font-size: 14px;
  font-weight: 700;

  &.real {
    color: #5DA2FF;
  }
  &.basic {
    color: #F86666;
  }
  &.kuku {
    color: #FFB42C;
  }
}

/* Tab Switcher */
.tab-switcher {
  display: flex;
  align-items: center;
  padding: 4px;
  margin: 16px 0 10px;
  background: #fff;
  border-radius: 24px;
}

.tab-item {
  flex: 1;
  height: 42px;
  line-height: 42px;
  text-align: center;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 700;
  color: #B8B8B8;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &.active {
    background: #FFB42C;
    color: #fff;
  }
}

/* Stats Card */
.stats-card {
  display: flex;
  justify-content: space-between;
  padding: 20px;
}

.stat-item {
  position: relative;
  display: grid;
  grid-template-columns: 32px minmax(58px, 72px);
  column-gap: 10px;
  flex: 1;
  align-items: center;
  justify-content: center;

  & + .stat-item::before {
    content: "";
    position: absolute;
    left: 0;
    top: 5px;
    width: 1px;
    height: 40px;
    background: #F0F0F0;
  }
}

.stat-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.stat-number {
  display: block;
  font-size: 28px;
  font-weight: 800;
  color: #F86666;
  line-height: 1.2;
}

.stat-label {
  display: block;
  margin-top: 2px;
  font-size: 15px;
  color: #333;
  font-weight: 700;
}

/* Battle Card */
.battle-card {
  padding: 18px 20px;
}

.battle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.battle-title {
  font-size: 15px;
  font-weight: 700;
  color: #F86666;
}

.battle-tabs {
  display: flex;
  gap: 0;
  padding: 3px;
  background: #F5F5F5;
  border-radius: 18px;
}

.battle-tab {
  min-width: 58px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  padding: 0 10px;
  border-radius: 14px;
  font-size: 12px;
  color: #B8B8B8;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;

  &.active {
    background: #fff;
    color: #F86666;
    font-weight: 700;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.06);
  }
}

.battle-list {
  display: flex;
  flex-direction: column;
}

.battle-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px 0;
  border-bottom: 1px solid #F0F0F0;

  &:last-child {
    border-bottom: none;
  }
}

.battle-info {
  flex: 1;
  min-width: 0;
}

.battle-tags {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 8px;
}

.battle-subject {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: #F86666;
}

.battle-chapter,
.battle-year {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #B8B8B8;
  background: #F6F6F6;
}

.battle-year {
  color: #F86666;
  background: #FFF0F0;
}

.battle-name {
  font-size: 15px;
  font-weight: 800;
  color: #333;
  line-height: 1.45;
  word-break: break-word;
}

.battle-count {
  display: flex;
  align-items: center;
  padding-left: 12px;
  font-size: 13px;
  white-space: nowrap;
}

.count-label {
  color: #FFB42C;
}

.count-value {
  color: #FFB42C;
  font-size: 20px;
  font-weight: 700;
}

.battle-more {
  text-align: center;
  padding: 8px 12px 0;
  font-size: 13px;
  color: #999;
  background: transparent;
  cursor: pointer;

  .arrow {
    margin-left: 4px;
    font-size: 10px;
  }
}

.battle-open-btn {
  width: 100%;
  height: 42px;
  margin-top: 16px;
  border: 1px solid #E8E8E8;
  border-radius: 14px;
  background: #fff;
  color: #F86666;
  font-size: 14px;
  font-weight: 700;
}

.empty-battle {
  padding: 22px 0;
  text-align: center;
  color: #B8B8B8;
  font-size: 13px;
}

/* Diagnosis Card */
.diagnosis-card {
  position: relative;
}

.diagnosis-header {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.diagnosis-icon {
  width: 36px;
  height: 36px;
  margin-right: 8px;
}

.diagnosis-title {
  font-size: 15px;
  font-weight: 700;
  color: #F86666;
}

.diagnosis-content {
  font-size: 14px;
  color: #333;
  line-height: 1.9;

  ::v-deep .diag-strong {
    color: #333;
    font-weight: 800;
  }

  ::v-deep .diag-list {
    margin: 6px 0;
    padding-left: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: 14px;
      margin-bottom: 6px;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 9px;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: #F86666;
      }
    }
  }
}

/* Quote Card */
.quote-card {
  position: relative;
  padding: 28px 20px 26px 48px;
  background: #fff;
}

.quote-mark {
  position: absolute;
  top: 16px;
  left: 16px;
  font-size: 54px;
  color: #E5E5E5;
  font-family: Georgia, serif;
  line-height: 1;
}

.quote-text {
  font-size: 13px;
  color: #999;
  line-height: 1.8;
}

/* Bottom Button */
.bottom-btn-wrapper {
  margin-top: 22px;
}

.bottom-btn {
  width: 100%;
  height: 56px;
  background: #FFB42C;
  color: #fff;
  border: none;
  border-radius: 18px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: none;

  &:active {
    opacity: 0.9;
    transform: scale(0.98);
  }
}

/* Loading & Error States */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #f15b5b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #999;
}

.error-text {
  font-size: 14px;
  color: #f15b5b;
  margin-bottom: 16px;
}

.btn-retry {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}
</style>
